import '../styles/tailwind.css';
import '../styles/global.scss';

import React, { useEffect } from 'react';
import App, { AppContext, AppProps } from 'next/app';
import Head from 'next/head';
import Router from 'next/router';
import { MicroFrontendProvider } from '@havenengineering/module-shared-micro-frontend-consumer/react';
import { AuthProvider } from '@havenengineering/module-shared-owners-ui/dist/contexts/auth';
import { FlagProvider } from '@unleash/nextjs/client';
import NProgress from 'nprogress';

import QualtricsSurveyIntercepts from '../components/QualtricsSurveyIntercepts/QualtricsSurveyIntercepts';
import { LettingsContextProvider } from '../contexts/lettings';
import { RuntimeConfigProvider } from '../contexts/runtimeConfig';
import { initializeDataDog, initializeGTM } from '../helpers/analytics';
import { getRuntimeVariables } from '../helpers/runtimeVariables';
import { useScrollToTopOnNavigate } from '../hooks/useScrollToTopOnNavigate';
import { MainLayout } from '../layouts/MainLayout';
import { MinimalLayout } from '../layouts/MinimalLayout';
import { AuthenticationWrapper } from '../wrappers/authentication';
import { RenewalsBlock } from '../wrappers/renewalsBlock';
import { SiteFeeHoldingWrapper } from '../wrappers/siteFeeHolding';

type RuntimeConfig = Awaited<ReturnType<typeof getRuntimeVariables>>;

// Populated on first client render; only read during client-side navigation,
// where process.env is unavailable. Guarded so SSR never mutates module state.
let cachedRuntimeConfig: RuntimeConfig | null = null;

interface OwnersAppProps extends AppProps {
  runtimeConfig: RuntimeConfig;
}

Router.events.on('routeChangeStart', () => NProgress.start());
Router.events.on('routeChangeComplete', () => NProgress.done());
Router.events.on('routeChangeError', () => NProgress.done());
NProgress.configure({ showSpinner: false });

export const OwnersApp = ({
  Component,
  pageProps,
  router,
  runtimeConfig,
}: OwnersAppProps) => {
  useScrollToTopOnNavigate();

  if (typeof window !== 'undefined' && runtimeConfig && !cachedRuntimeConfig) {
    cachedRuntimeConfig = runtimeConfig;
  }

  const {
    PUBLIC_GTM_ID,
    PUBLIC_GTM_AUTH,
    PUBLIC_GTM_PREVIEW,
    PUBLIC_ENV,
    PUBLIC_OWNERS_NAVIGATION_FRAGMENT_URL,
    PUBLIC_OWNERS_NAVIGATION_FRAGMENT_CLUSTER_URL,
    PUBLIC_UNLEASH_FRONTEND_API_URL,
    UNLEASH_FRONTEND_API_TOKEN,
    PUBLIC_UNLEASH_APP_NAME,
    PUBLIC_DD_VERSION,
    PUBLIC_DD_RUM_APPLICATION_ID,
    PUBLIC_DD_SERVICE,
    PUBLIC_DD_DD_ENV,
    PUBLIC_DD_RUM_CLIENT_TOKEN,
    PUBLIC_DD_RUM_SAMPLE_RATE,
    PUBLIC_DD_RUM_DEFAULT_PRIVACY_LEVEL,
    PUBLIC_DD_RUM_REPLAY_SAMPLE_RATE,
  } = runtimeConfig;

  const microFrontends = {
    navigation: {
      url: PUBLIC_OWNERS_NAVIGATION_FRAGMENT_URL || '',
      clusterUrl: PUBLIC_OWNERS_NAVIGATION_FRAGMENT_CLUSTER_URL || '',
    },
  };

  // Initialize GTM and Datadog on client side
  useEffect(() => {
    if (PUBLIC_GTM_ID) {
      initializeGTM({
        gtmId: PUBLIC_GTM_ID,
        auth: PUBLIC_GTM_AUTH,
        preview: PUBLIC_GTM_PREVIEW,
      });
    }

    if (PUBLIC_DD_RUM_APPLICATION_ID) {
      initializeDataDog({
        env: PUBLIC_DD_DD_ENV,
        applicationId: PUBLIC_DD_RUM_APPLICATION_ID,
        clientToken: PUBLIC_DD_RUM_CLIENT_TOKEN,
        defaultPrivacyLevel: PUBLIC_DD_RUM_DEFAULT_PRIVACY_LEVEL,
        version: PUBLIC_DD_VERSION,
        service: PUBLIC_DD_SERVICE,
        sampleRate: PUBLIC_DD_RUM_SAMPLE_RATE,
        replaySampleRate: PUBLIC_DD_RUM_REPLAY_SAMPLE_RATE,
      });
    }
  }, [
    PUBLIC_GTM_ID,
    PUBLIC_GTM_AUTH,
    PUBLIC_GTM_PREVIEW,
    PUBLIC_DD_DD_ENV,
    PUBLIC_DD_RUM_APPLICATION_ID,
    PUBLIC_DD_RUM_CLIENT_TOKEN,
    PUBLIC_DD_RUM_DEFAULT_PRIVACY_LEVEL,
    PUBLIC_DD_VERSION,
    PUBLIC_DD_SERVICE,
    PUBLIC_DD_RUM_SAMPLE_RATE,
    PUBLIC_DD_RUM_REPLAY_SAMPLE_RATE,
  ]);

  const headTitle = `Haven Owners${
    pageProps.pageTitle ? ` - ${pageProps.pageTitle}` : ''
  }`;

  useEffect(() => {
    const jssStyles = document.querySelector('#jss-server-side');
    if (jssStyles && jssStyles.parentElement) {
      jssStyles.parentElement.removeChild(jssStyles);
    }
  }, []);

  if (!pageProps.pageTitle) {
    console.warn('Page title missing for the path ', router.pathname);
  }

  const index =
    PUBLIC_ENV !== 'production' || router.pathname !== '/'
      ? 'noindex'
      : 'index';
  const unleashConfig = {
    url: PUBLIC_UNLEASH_FRONTEND_API_URL,
    clientKey: UNLEASH_FRONTEND_API_TOKEN,
    appName: PUBLIC_UNLEASH_APP_NAME,
  };
  const renderApp = () => {
    if (router.pathname === '/maintenance') return <Component />;
    if (router.pathname === '/account/verify-email')
      return (
        <MinimalLayout>
          <Component {...pageProps} key={router.asPath} />
        </MinimalLayout>
      );
    return (
      <MainLayout>
        <Component {...pageProps} key={router.asPath} />
      </MainLayout>
    );
  };

  return (
    <RuntimeConfigProvider config={runtimeConfig}>
      <FlagProvider config={unleashConfig}>
        <div>
          <Head>
            <title>{headTitle}</title>
            <meta
              name="viewport"
              content="minimum-scale=1, initial-scale=1, width=device-width"
            />
            <meta name="robots" content={`${index},nofollow`} />
            <meta name="googlebot" content={`${index},nofollow`} />
          </Head>
          <MicroFrontendProvider microFrontends={microFrontends}>
            <AuthProvider>
              <AuthenticationWrapper
                availableForGuests={pageProps.availableForGuests}
              >
                <LettingsContextProvider>
                  <SiteFeeHoldingWrapper
                    showSiteFeeHolding={pageProps.isSiteFeeHolding}
                  >
                    <RenewalsBlock>
                      <>
                        <QualtricsSurveyIntercepts />
                        {renderApp()}
                      </>
                    </RenewalsBlock>
                  </SiteFeeHoldingWrapper>
                </LettingsContextProvider>
              </AuthenticationWrapper>
            </AuthProvider>
          </MicroFrontendProvider>
        </div>
      </FlagProvider>
    </RuntimeConfigProvider>
  );
};

OwnersApp.getInitialProps = async (appContext: AppContext) => {
  const appProps = await App.getInitialProps(appContext);

  if (typeof window !== 'undefined') {
    // Client-side navigation: process.env vars are unavailable in the browser,
    // so reuse the config cached during the first client render.
    if (!cachedRuntimeConfig) {
      throw new Error(
        'Runtime config cache was not populated on initial client render.'
      );
    }
    return { ...appProps, runtimeConfig: cachedRuntimeConfig };
  }

  const runtimeConfig = await getRuntimeVariables();
  return { ...appProps, runtimeConfig };
};

export default OwnersApp;
